import { create } from '@bufbuild/protobuf'
import { useQuery } from '@connectrpc/connect-query'
import { getJepxMemberInfo } from '@ebalance/proto/bffuser/v1/jepx_member_info_service-JepxMemberInfoService_connectquery'
import { GetJepxMemberInfoRequestSchema } from '@ebalance/proto/bffuser/v1/jepx_member_info_service_pb'

import { feeUnitPriceToYenPerKwh, resolveFeeUnitPrice } from '~/models/jepxFee'

export type SpotFeeYenPerKwhState = {
  /** RPC の取得状態 (react-query の isPending/isError/isSuccess を丸めたもの)。 */
  status: 'loading' | 'error' | 'success'
  /**
   * スポット市場 取引手数料単価 (円/kWh、 SoT: `docs/specs/admin/integration/jepx-member-info.md`
   * AC-IJM-007a)。 status が `'success'` でも undefined になることがある (下記参照)。
   */
  feeYenPerKwh: number | undefined
  /**
   * JEPX 会員 ID を登録済みの組織か (`GetJepxMemberInfo` の `memberInfo` の有無、 AC-IJM-003)。
   * 取得が成功するまでは undefined。 会員 ID 未登録の組織は待っても手数料単価が現れないので、
   * 押せない理由の文を「時間をおいて」ではなく運営への案内にする呼び出し側
   * (入札一覧の CSV ダウンロード、 AC-BSP-100) が見分けるために使う。
   */
  memberRegistered: boolean | undefined
}

/**
 * スポット市場 取引手数料単価 (円/kWh) を JEPX 連携マスタ情報 (BFF
 * `JepxMemberInfoService.GetJepxMemberInfo`) から取得する page-local hook (AC-BSP-100)。
 *
 * 本単価は手数料自動考慮 (AC-BSP-004b) の入札価格の算出に使う。 収支の計算には使わない (AC-BSP-090)。
 * `feeYenPerKwh` が undefined になるのは次のいずれかで、 AC-BSP-100 はこれらを表示上
 * 区別しない (実際の表示切替 — 「集計未完了」 / 明細セルの「取得中」 / CSV 出力無効化 — は
 * この hook の消費側の責務):
 *   - ロード中、 または取得失敗後の自動リトライ中 (`status` が `'loading'` / `'error'`)
 *   - JEPX 会員 ID が未登録 (AC-IJM-003)。 手数料単価は運営が会員登録に紐付けて設定する値で、
 *     未登録組織にはまだ存在しないものとして扱う (`docs/specs/bidding/spot-bidding.md:973`)。
 *     fee_schedule 自体は member_info 未設定でも常に返る (BFF handler の godoc 参照) ため、
 *     `resolveFeeUnitPrice` (`~/models/jepxFee`、 入札制限画面と共通) で明示的に弾く
 *   - `fee_schedule.spot_trading_fee_per_kwh` が空文字・非数値、 または 0.01 円/kWh の整数倍で
 *     ない (`feeUnitPriceToYenPerKwh`。 backend の取込と同じく使えない単価として扱い、 丸めて
 *     使わない。 作成時の加算と入札一覧の CSV の逆算の両方がこの値を使う)
 *
 * 全画面エラー化を避けるため `throwOnError: false` を明示する (`useSpotBidLimits` と同じ
 * 理由。 global QueryClient の既定は `throwOnError: true`、 issue #7070)。 retry は
 * `ApiClientProvider` の `shouldRetryQuery` / `queryRetryDelay` に委譲する
 * (一過性エラーのみ有限回リトライ、 それ以外は即座に `error` へ確定する)。
 */
export function useSpotFeeYenPerKwh(): SpotFeeYenPerKwhState {
  const { data, isPending, isError } = useQuery(
    getJepxMemberInfo,
    create(GetJepxMemberInfoRequestSchema, {}),
    { throwOnError: false },
  )

  const status: SpotFeeYenPerKwhState['status'] = isPending
    ? 'loading'
    : isError
      ? 'error'
      : 'success'

  const feeYenPerKwh =
    status === 'success'
      ? feeUnitPriceToYenPerKwh(resolveFeeUnitPrice(data, 'spot'))
      : undefined

  const memberRegistered =
    status === 'success' ? data?.memberInfo !== undefined : undefined

  return { status, feeYenPerKwh, memberRegistered }
}
